<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import dealReportsAPI from 'dashboard/api/dealReports';
import DoughnutChart from 'shared/components/charts/DoughnutChart.vue';
import TableHeaderCell from './TableHeaderCell.vue';
import { formatCurrencyBRL } from './commercialFormatters';
import {
  doughnutDataset,
  doughnutOptions,
  paletteFor,
} from '../charts/chartDesign';

const props = defineProps({
  params: { type: Object, required: true },
});

const { t } = useI18n();
const data = ref(null);
const isLoading = ref(true);
const hasError = ref(false);

const fetchData = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const response = await dealReportsAPI.getLossReasons(props.params);
    data.value = response.data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.params, fetchData, { immediate: true, deep: true });

const rows = computed(() => data.value?.rows || []);

const labelFor = row =>
  row.label || t('DASHBOARD.COMERCIAL.LOSS_REASONS.NO_REASON');

const slices = computed(() =>
  rows.value.map((row, index) => ({
    label: labelFor(row),
    percentage: row.percentage,
    color: paletteFor(index),
  }))
);

const topSlice = computed(() =>
  rows.value.reduce(
    (top, row, index) => (row.count > rows.value[top].count ? index : top),
    0
  )
);

const chartCollection = computed(() => ({
  labels: slices.value.map(slice => slice.label),
  datasets: [
    doughnutDataset({
      data: rows.value.map(row => row.count),
      colors: slices.value.map(slice => slice.color.hex),
    }),
  ],
}));

const chartOptions = computed(() => doughnutOptions({ formatTick: String }));
</script>

<template>
  <section class="border rounded-lg border-n-weak">
    <h2 class="p-3 font-medium text-n-slate-12">
      {{ t('DASHBOARD.COMERCIAL.SECTIONS.LOSS_REASONS') }}
    </h2>

    <p v-if="hasError" class="px-3 pb-3 text-sm text-n-ruby-9">
      {{ t('DASHBOARD.COMERCIAL.LOAD_ERROR') }}
      <button type="button" class="underline" @click="fetchData">
        {{ t('DASHBOARD.COMERCIAL.RETRY') }}
      </button>
    </p>
    <p v-else-if="isLoading" class="px-3 pb-3 text-sm text-n-slate-11">
      {{ t('DASHBOARD.COMERCIAL.LOADING') }}
    </p>
    <p v-else-if="!rows.length" class="px-3 pb-3 text-sm text-n-slate-10">
      {{ t('DASHBOARD.COMERCIAL.EMPTY_STATE') }}
    </p>
    <div
      v-else
      class="flex flex-col gap-4 px-3 pb-3 md:flex-row md:items-start"
    >
      <div class="flex-1 overflow-y-auto overflow-x-auto max-h-80">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-10">
              <th class="py-1 pr-3">
                <TableHeaderCell
                  :label="t('DASHBOARD.COMERCIAL.LOSS_REASONS.LABEL')"
                  :tooltip="t('DASHBOARD.COMERCIAL.TOOLTIPS.LOSS_REASON_LABEL')"
                />
              </th>
              <th class="py-1 pr-3">
                <TableHeaderCell
                  :label="t('DASHBOARD.COMERCIAL.LOSS_REASONS.COUNT')"
                  :tooltip="t('DASHBOARD.COMERCIAL.TOOLTIPS.LOSS_REASON_COUNT')"
                />
              </th>
              <th class="py-1 pr-3">
                <TableHeaderCell
                  :label="t('DASHBOARD.COMERCIAL.LOSS_REASONS.PERCENTAGE')"
                  :tooltip="
                    t('DASHBOARD.COMERCIAL.TOOLTIPS.LOSS_REASON_PERCENTAGE')
                  "
                />
              </th>
              <th class="py-1 pr-3">
                <TableHeaderCell
                  :label="t('DASHBOARD.COMERCIAL.LOSS_REASONS.VALUE')"
                  :tooltip="t('DASHBOARD.COMERCIAL.TOOLTIPS.LOSS_REASON_VALUE')"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in rows"
              :key="index"
              class="border-t border-n-weak"
            >
              <td class="py-1 pr-3 text-n-slate-12">{{ labelFor(row) }}</td>
              <td class="py-1 pr-3">{{ row.count }}</td>
              <td class="py-1 pr-3">{{ row.percentage }}%</td>
              <td class="py-1 pr-3">{{ formatCurrencyBRL(row.value) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="items-center hidden h-48 gap-3 shrink-0 md:flex md:w-80">
        <div class="relative flex-1 h-full min-w-0">
          <DoughnutChart
            :collection="chartCollection"
            :chart-options="chartOptions"
          />
          <div
            data-doughnut-center
            class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
          >
            <span class="text-lg font-semibold leading-none text-n-slate-12">
              {{ slices[topSlice].percentage }}%
            </span>
            <span
              class="max-w-[4.5rem] mt-0.5 text-[10px] truncate text-n-slate-11"
            >
              {{ slices[topSlice].label }}
            </span>
          </div>
        </div>
        <ul
          class="flex flex-col flex-1 max-h-full gap-1.5 p-0 m-0 overflow-y-auto list-none min-w-0"
        >
          <li
            v-for="(slice, index) in slices"
            :key="index"
            data-legend-item
            class="flex items-center gap-1.5 text-xs min-w-0"
          >
            <span
              class="rounded-full size-2 shrink-0"
              :class="slice.color.dot"
            />
            <span class="truncate text-n-slate-11" :title="slice.label">
              {{ slice.label }}
            </span>
            <span class="ml-auto font-medium tabular-nums text-n-slate-12">
              {{ slice.percentage }}%
            </span>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>
